import { getPlatform, isGlobalCasino, Platform } from '../PlatformUtils'
import { FeatureToggleService } from './FeatureToggleService'
import { Phase } from '../communication/customer/PlayerFeatureConfig'

export enum Feature {
	SWITCH_CURRENCY = 'SWITCH_CURRENCY',
	SWEEPS_COIN_POKER_PLAY = 'SWEEPS_COIN_POKER_PLAY',
	ALL_ACHIEVEMENTS = 'ALL_ACHIEVEMENTS',
	SLOTS_LOBBY = 'SLOTS_LOBBY',
	SWEEPS_COIN_CASINO_GAMES = 'SWEEPS_COIN_CASINO_GAMES',
	NOTIFICATIONS_WIDGET = 'NOTIFICATIONS_WIDGET',
	TOURNAMENT_NOTIFICATIONS = 'TOURNAMENT_NOTIFICATIONS',
	FIRST_TIME_OFFER = 'FIRST_TIME_OFFER',
	DAILY_BONUS_SC = 'DAILY_BONUS_SC',
	DAILY_BONUS = 'DAILY_BONUS',
	VERIFICATION_BONUS = 'VERIFICATION_BONUS',
	FREE_TO_PLAY_INFO = 'FREE_TO_PLAY_INFO',
	REDEMPTIONS = 'REDEMPTIONS',
	NOTIFICATIONS = 'NOTIFICATIONS',
	SWEEPS_PACKAGES = 'SWEEPS_PACKAGES',
	VIEW_SWEEPS_GAMEPLAY_HISTORY = 'VIEW_SWEEPS_GAMEPLAY_HISTORY',
	VIEW_SWEEPS_TRANSACTION_HISTORY = 'VIEW_SWEEPS_TRANSACTION_HISTORY',
	SELF_TIMEOUT = 'SELF_TIMEOUT',
	VIEW_TICKETS = 'VIEW_TICKETS',
	SWEEPS_ITEMS = 'SWEEPS_ITEMS',
	PURCHASE_HISTORY = 'PURCHASE_HISTORY',
	SWEEPSTAKES_INFO = 'SWEEPSTAKES_INFO',
	CANCEL_REDEMPTION = 'CANCEL_REDEMPTION',
	POKER = 'POKER',
	LEADERBOARDS = 'LEADERBOARDS',
	SWEEPS_LEADERBOARDS = 'SWEEPS_LEADERBOARDS',
	CHALLENGES = 'CHALLENGES',
	SWEEPS_CHALLENGES = 'SWEEPS_CHALLENGES',
	SWEEPS_ACHIEVEMENTS = 'SWEEPS_ACHIEVEMENTS',
	BONUS_CODES = 'BONUS_CODES',
	PRIZE_DRAW = 'PRIZE_DRAW',
	ENDLESS_PLAY = 'ENDLESS_PLAY',
	SWEEPS_GIFT_SPINS = 'SWEEPS_GIFT_SPINS',
	CASHIER_BUY_COINS = 'CASHIER_BUY_COINS',
	SWEEPS_VAULT = 'SWEEPS_VAULT',
	GOLD_VAULT = 'GOLD_VAULT',
	GC_AND_SC_PLAY = 'GC_AND_SC_PLAY',
	SC_HAND_COUNT = 'SC_HAND_COUNT',
	SC_REFER_A_FRIEND = 'SC_REFER_A_FRIEND',
}

const BLOCKED_MARKET_REMOVED_FEATURES = [
	Feature.VERIFICATION_BONUS,
	Feature.FIRST_TIME_OFFER,
	Feature.SWEEPS_PACKAGES,
	Feature.BONUS_CODES,
	Feature.PRIZE_DRAW,
	Feature.CASHIER_BUY_COINS,
	Feature.SWEEPS_VAULT,
	Feature.CHALLENGES,
	Feature.FREE_TO_PLAY_INFO,
	Feature.ALL_ACHIEVEMENTS,
	Feature.LEADERBOARDS,
	Feature.ENDLESS_PLAY,
	Feature.GOLD_VAULT,
]

const PROMO_PLAY_BLOCKED_MARKET_PHASE_1_REMOVED_FEATURES = [
	Feature.DAILY_BONUS_SC,
	Feature.DAILY_BONUS,
	Feature.FIRST_TIME_OFFER,
	Feature.SWEEPS_PACKAGES,
	Feature.BONUS_CODES,
	Feature.PRIZE_DRAW,
	Feature.SWEEPS_VAULT,
	Feature.FREE_TO_PLAY_INFO,
	Feature.SWEEPS_CHALLENGES,
	Feature.SWEEPS_ACHIEVEMENTS,
]

const PROMO_PLAY_BLOCKED_MARKET_PHASE_2_REMOVED_FEATURES = [
	...PROMO_PLAY_BLOCKED_MARKET_PHASE_1_REMOVED_FEATURES,
	Feature.SWITCH_CURRENCY,
	Feature.SWEEPS_COIN_POKER_PLAY,
	Feature.SWEEPS_COIN_CASINO_GAMES,
	Feature.SWEEPS_GIFT_SPINS,
	Feature.SC_HAND_COUNT,
	Feature.SWEEPS_LEADERBOARDS,
	Feature.SWEEPS_ITEMS,
	Feature.SWEEPSTAKES_INFO,
	Feature.NOTIFICATIONS_WIDGET,
	Feature.TOURNAMENT_NOTIFICATIONS,
	Feature.SC_REFER_A_FRIEND,
]

const PROMO_PLAY_BLOCKED_MARKET_PHASE_3_REMOVED_FEATURES = [
	...PROMO_PLAY_BLOCKED_MARKET_PHASE_2_REMOVED_FEATURES,
	Feature.REDEMPTIONS,
	Feature.CANCEL_REDEMPTION,
	Feature.VIEW_SWEEPS_TRANSACTION_HISTORY,
	Feature.VIEW_SWEEPS_GAMEPLAY_HISTORY,
]

const GC_ONLY_MARKET_REMOVED_FEATURES = [
	Feature.SWITCH_CURRENCY,
	Feature.SWEEPS_COIN_POKER_PLAY,
	Feature.SWEEPS_COIN_CASINO_GAMES,
	Feature.DAILY_BONUS_SC,
	Feature.VERIFICATION_BONUS,
	Feature.FREE_TO_PLAY_INFO,
	Feature.FIRST_TIME_OFFER,
	Feature.SWEEPS_PACKAGES,
	Feature.CANCEL_REDEMPTION,
	Feature.BONUS_CODES,
	Feature.REDEMPTIONS,
	Feature.PRIZE_DRAW,
	Feature.SWEEPS_GIFT_SPINS,
	Feature.SWEEPS_VAULT,
	Feature.SC_HAND_COUNT,
	Feature.SC_REFER_A_FRIEND,
]

const CASINO_ONLY_BLOCKED_MARKET_REMOVED_FEATURES = [
	Feature.SWEEPS_COIN_CASINO_GAMES,
	Feature.SWEEPS_GIFT_SPINS,
]

const PHASE1_REMOVED_FEATURES = [
	Feature.VERIFICATION_BONUS,
	Feature.FIRST_TIME_OFFER,
	Feature.SWEEPS_PACKAGES,
	Feature.BONUS_CODES,
	Feature.PRIZE_DRAW,
	Feature.CASHIER_BUY_COINS,
	Feature.SWEEPS_VAULT,
	Feature.CHALLENGES,
	Feature.FREE_TO_PLAY_INFO,
	Feature.ALL_ACHIEVEMENTS,
	Feature.LEADERBOARDS,
	Feature.ENDLESS_PLAY,
	Feature.GOLD_VAULT,
	Feature.DAILY_BONUS_SC,
	Feature.PRIZE_DRAW,
	Feature.SWEEPS_GIFT_SPINS,
	Feature.SC_HAND_COUNT,
	Feature.TOURNAMENT_NOTIFICATIONS,
	Feature.SWEEPSTAKES_INFO,
	Feature.SC_REFER_A_FRIEND,
]

const PHASE2_REMOVED_FEATURES = [
	...PHASE1_REMOVED_FEATURES,
	Feature.SWITCH_CURRENCY,
	Feature.SWEEPS_COIN_POKER_PLAY,
	Feature.SWEEPS_COIN_CASINO_GAMES,
	Feature.GC_AND_SC_PLAY,
]

export class UserFeatureService {
	private readonly platform: Platform
	private readonly featureSet = new Set<Feature>()
	private isPhase2Restricted: boolean = false
	private isPromoPlayRestricted: boolean = false

	constructor(platform: Platform) {
		this.platform = platform
	}

	get isUserPhase2Restricted() {
		return this.isPhase2Restricted
	}

	get isUserPromoPlayRestricted() {
		return this.isPromoPlayRestricted
	}

	generateAvailableFeatureAccess(residentialState: string, geoLocationState: string) {
		this.featureSet.clear()

		for (const feature in Feature) {
			this.featureSet.add(feature as Feature)
		}

		if (FeatureToggleService.blockMarketFeature.isEnabled(residentialState, geoLocationState)) {
			const isPromoPlayBlockedMarket = FeatureToggleService.blockMarketFeature.isPromoPlayOnlyMarket(
				residentialState,
				geoLocationState,
			)

			if (isPromoPlayBlockedMarket) this.isPromoPlayRestricted = true

			let blockedFeatures: Feature[]

			if (isPromoPlayBlockedMarket) {
				if (
					FeatureToggleService.blockMarketFeature.getPromoPlayPhase3Info(residentialState, geoLocationState).enabled
				) {
					blockedFeatures = PROMO_PLAY_BLOCKED_MARKET_PHASE_3_REMOVED_FEATURES
				} else if (
					FeatureToggleService.blockMarketFeature.getPromoPlayPhase2Info(residentialState, geoLocationState).enabled
				) {
					blockedFeatures = PROMO_PLAY_BLOCKED_MARKET_PHASE_2_REMOVED_FEATURES
				} else {
					blockedFeatures = PROMO_PLAY_BLOCKED_MARKET_PHASE_1_REMOVED_FEATURES
				}
			} else {
				blockedFeatures = [...BLOCKED_MARKET_REMOVED_FEATURES]
				if (FeatureToggleService.blockMarketFeature.isGameplayBlockEnabled(residentialState, geoLocationState)) {
					blockedFeatures.push(Feature.GC_AND_SC_PLAY)
				}
			}

			this.removeFeatures(blockedFeatures)
		}

		if (FeatureToggleService.gcOnlyMarketFeature.isEnabled(residentialState)) {
			this.removeFeatures(GC_ONLY_MARKET_REMOVED_FEATURES)
		}

		if (FeatureToggleService.casinoOnlyBlockedMarketFeature.isEnabled(residentialState, geoLocationState)) {
			this.removeFeatures(CASINO_ONLY_BLOCKED_MARKET_REMOVED_FEATURES)
		}
	}

	removePhaseRestrictedFeatures(userId: string, phase: Phase) {
		switch (phase) {
			case 'PHASE_1':
				this.removeFeatures(PHASE1_REMOVED_FEATURES)
				break
			case 'PHASE_2':
				this.isPhase2Restricted = true
				this.removeFeatures(PHASE2_REMOVED_FEATURES)
				break
			case 'PHASE_3':
				this.isPhase2Restricted = true
				this.removeFeatures(PHASE2_REMOVED_FEATURES)
				break
			case 'NOT_IN_PHASE':
			default:
				if (FeatureToggleService.playerFeatureConfig.isPlayerRestrictedInPhase1(userId)) {
					this.removeFeatures(PHASE1_REMOVED_FEATURES)
				}

				if (FeatureToggleService.playerFeatureConfig.isPlayerRestrictedInPhase2(userId)) {
					this.removeFeatures(PHASE2_REMOVED_FEATURES)
				}
				break
		}
	}

	doesUserHaveFeature(feature: Feature): boolean {
		return this.featureSet.has(feature as Feature)
	}

	private removeFeatures(features: Feature[]) {
		for (const feature of features) {
			this.featureSet.delete(feature as Feature)
		}
	}
}

export const getPackagePlatformId = () => {
	if (isGlobalCasino()) return 'GLOBALCASINO_DOT_COM'
	return 'GLOBALPOKER_DOT_COM'
}

export const getPlayType = (): PlayType => {
	return userFeatureService.doesUserHaveFeature(Feature.SWEEPS_PACKAGES) ? 'PROMOTIONAL' : 'STANDARD'
}

export const userFeatureService = new UserFeatureService(getPlatform())
